import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { PiFileCsv } from "react-icons/pi";

import Dropzone from "@/components/common/Dropzone/Dropzone";
import FileScanner from "@/components/common/Dropzone/FileScanner";
import MotionText from "@/components/common/Motion/MotionText";
import MotionDiv from "@/components/common/Motion/MotionDiv";
import HeaderBack from "@/components/dashboard/Header/HeaderBack";
import { UberIcon } from "@/components/common/UberIcon";

import ProgressBar from "@/components/common/ProgressBar/ProgressBar";

import { useKycStore } from "@/store/useKycStore";
import { useUberStore } from "@/store/useUberStore";
import { usePageSetup } from "@/hooks/usePageSetup";
import { parseCSV } from "@/utils/csv";

export const UberTripsPage = () => {
  const { t } = useTranslation()
  usePageSetup(t('uber.pageTitle.trips'))
  /* Reusing KYC */
  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(2), [])

  const navigate = useNavigate();

  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setTripAnalysis = useUberStore((state) => state.setTripAnalysis)

  /* We can easily parse the CSV file in frontend */
  const analyzeFile = async (file: File) => {
    try {
      const text = await file.text();
      const rows = parseCSV(text);

      const dataRows = rows.slice(1);

      const line_items = dataRows
        .map(row => ({
          reference: row[0],
          date: row[1],
          amount: parseFloat(row[17])
        }))
        .filter(item => item.reference && !isNaN(item.amount));

      if (line_items.length === 0) throw new Error('Empty CSV');

      return { line_items };
    } catch (error) {
      console.log(error)
      setError(t('uber.errors.fileUploadFailed'));
      setIsScanning(false);
      return null;
    }
  };

  const handleFileSelect = async (file: File) => {
    setIsScanning(true);
    setError(null);

    const tripAnalysis = await analyzeFile(file);

    if (tripAnalysis) {
      setTripAnalysis(tripAnalysis.line_items)
      navigate("/uber/income");
    }
  };

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderBack>
        <div className="flex flex-row items-center w-[8.5rem] ml-[3.84rem]">
          <ProgressBar
            currentStep={progress}
            totalSteps={3}
          />
        </div>
      </HeaderBack>
      <div className="h-full flex flex-col px-5 pb-[10rem]">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900 mb-3"
          delay={0.1}
        >
          {t('uber.trips.title')}
        </MotionText>
        <MotionText
          as="p"
          className="text-zinc-700 text-sm mb-8"
          delay={0.2}
        >
          {t('uber.trips.description')}
        </MotionText>
        <MotionDiv
          className="bg-gray-100 p-4 rounded-lg flex flex-row gap-2 mb-4"
          delay={0.2}
        >
          <PiFileCsv className="h-6 w-6 text-black shrink-0" />
          <div className="flex flex-col gap-1">
            <p className="text-sm text-zinc-700 font-semibold">
              {t('uber.trips.downloadStep')}
            </p>
            <p className="text-sm text-zinc-700">
              {t('uber.trips.downloadInstructions')}
            </p>
          </div>
        </MotionDiv>
        {error && (
          <MotionText
            as="p"
            className="text-red-500 text-sm"
            delay={0.2}
          >
            {error}
          </MotionText>
        )}
        <MotionDiv
          className="flex flex-col gap-2"
          delay={0.3}
        >
          {isScanning ? (
            <FileScanner isScanning={isScanning} />
          ) : (
            <Dropzone
              type="csv"
              onFileSelect={handleFileSelect}
              maxSize={10 * 1024 * 1024}
              buttonText={t('uber.trips.selectFile')}
            >
              <UberIcon size="lg" className="mb-2" />
            </Dropzone>
          )}
        </MotionDiv>
      </div>
    </div>
  );
};

export default UberTripsPage;
